import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Container, Link } from '@mui/material';
import {
  AutoAwesome as LogoIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';

const Footer: React.FC = () => {
  const footerLinks = {
    Product: [
      { label: 'Features', href: '/#features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Integrations', href: '/integrations' },
    ],
    Resources: [
      { label: 'Blog', href: '/blog' },
      { label: 'Help Center', href: '/help' },
      { label: 'Contact', href: '/contact' },
    ],
    Company: [
      { label: 'About', href: '/' },
      { label: 'Industries', href: '/#industries' },
    ],
    Legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
    ],
  };

  return (
    <Box
      component="footer"
      sx={{
        background: tokens.background.dark,
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 5 },
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: '2fr repeat(4, 1fr)',
            },
            gap: { xs: 4, md: 6 },
            mb: { xs: 4, md: 6 },
          }}
        >
          {/* Brand Column */}
          <Box sx={{ gridColumn: { md: 'span 1' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: tokens.primary.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LogoIcon sx={{ fontSize: 24, color: 'white' }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Chairs Up
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.7,
                maxWidth: 280,
                mb: 3,
              }}
            >
              Analytics that read your connected sales data, flag the days that do not fit the pattern, and rank the customers going quiet.
            </Typography>
          </Box>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <Box key={category}>
              <Typography
                variant="overline"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  mb: 2,
                  display: 'block',
                }}
              >
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {links.map((link, index) => (
                  <Link
                    key={index}
                    component={link.href.startsWith('/') ? RouterLink : 'a'}
                    to={link.href.startsWith('/') ? link.href : undefined}
                    href={!link.href.startsWith('/') ? link.href : undefined}
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'color 0.2s ease',
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            pt: { xs: 3, md: 4 },
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.4)' }}
          >
            © {new Date().getFullYear()} Chairs Up. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="mailto:hello@dynamicdecisionsai.com"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                textDecoration: 'none',
                fontSize: '0.85rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              hello@dynamicdecisionsai.com
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
